import { Component, type ErrorInfo, type ReactNode } from "react";

import { useT } from "../shared/i18n";
import { ActionBar, Button, Icons } from "../shared/ui";
import {
  CorrelationRef,
  IconRing,
  type StateTone,
} from "../shared/ui/Feedback";
import type { IconProps } from "../shared/ui/icons";
import type { RecoveryKind } from "./recoveryKinds";

const GLYPH: Record<RecoveryKind, (props: IconProps) => ReactNode> = {
  host_unsupported: Icons.Telegram,
  session_rejected: Icons.SignIn,
  auth_expired: Icons.SignIn,
  nav_token_expired: Icons.LinkExpired,
  offline: Icons.Offline,
  backend_error: Icons.ServerDown,
  render_error: Icons.Warning,
};

const TONE: Record<RecoveryKind, StateTone> = {
  host_unsupported: "accent",
  session_rejected: "accent",
  auth_expired: "accent",
  nav_token_expired: "accent",
  offline: "warning",
  backend_error: "error",
  render_error: "error",
};

/**
 * Full-screen recovery state: what happened, that data is safe, and the one
 * command that gets the owner going again. `onAction` absent means there is
 * nothing the app itself can do (no bot link configured).
 */
export function RecoveryScreen({
  kind,
  onAction,
  correlationId,
}: {
  kind: RecoveryKind;
  onAction?: () => void;
  correlationId?: string;
}) {
  const t = useT();
  const Glyph = GLYPH[kind];
  return (
    <div className="flex min-h-screen flex-col bg-canvas text-fg">
      <main
        role="alert"
        className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <IconRing icon={Glyph} tone={TONE[kind]} />
        <h1 className="text-title text-fg">{t(`recovery.${kind}.title`)}</h1>
        <p className="max-w-sm text-body text-fg-muted">
          {t(`recovery.${kind}.body`)}
        </p>
        {correlationId && (
          <CorrelationRef
            correlationId={correlationId}
            label={t("error.reference")}
            copyLabel={t("error.reference.copy")}
            copiedLabel={t("error.reference.copied")}
          />
        )}
      </main>
      {onAction && (
        <ActionBar>
          <Button onClick={onAction}>{t(`recovery.${kind}.action`)}</Button>
        </ActionBar>
      )}
    </div>
  );
}

/** Last line of defence: a crashed render becomes the render_error screen. */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("render_failed", error, info.componentStack);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <RecoveryScreen
        kind="render_error"
        onAction={() => {
          window.location.reload();
        }}
      />
    );
  }
}
